import React, { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import AtlasV2MetricListContainer from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2MetricListContainer';
import { IconsList } from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTableHeader';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { EntityLink, TableSpan } from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import ProductGrid from 'src/components/BeaconV2Components/ProductGrid/ProductGrid';
import ProductCell from 'src/components/BeaconV2Components/ProductCell/ProductCell';
import { useDorisTableData } from 'src/serverProxy/useDorisTableData';
import { useBrandEntity, useProductEntity, useRetailerId } from 'src/utils/Hooks/reduxSelectors';
import { formatTime } from 'src/utils/dateformatting';
import { getStartAndEndOfWeek } from 'src/utils/dateUtils';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { useComparisonTimePeriodShortDisplayName } from 'src/utils/Hooks/typedHooks';
import { useMainTimePeriodShortDisplayName } from 'src/utils/Hooks/typedHooks';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import AtlasV2DorisTableTrafficV2ExportModal from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2DorisTableTrafficV2ExportModal';
import { getBrandImageUrl } from 'src/utils/image';
import { BeaconProImageFallback } from 'src/components/SvgIcons/SvgIcons';
import {
  useChartContextDisableSubtitleFallback,
  useChartContextHideSubtitle
} from 'src/utils/chartContext/ChartContextGroupContext';
import { useAppSelector } from 'src/utils/Hooks';
import { buildSubtitleDisplayName } from 'src/utils/filters';
import { useBeaconFilters } from 'src/utils/Hooks/useDorisApplyEntityFilters';
import { getAppName } from 'src/utils/app';
import { AppName } from 'sl-api-connector';

const brandColumnImageStyles: CSSProperties = {
  width: '30px',
  height: '30px',
  marginRight: '12px',
  objectFit: 'contain'
};

export enum TrafficDropdownOption {
  Keyword = 'keyword',
  Product = 'product',
  Brand = 'brand',
  WeekId = 'weekId'
}

export const subTitleDisplayName = {
  [TrafficDropdownOption.Keyword]: 'keyword',
  [TrafficDropdownOption.Product]: 'product',
  [TrafficDropdownOption.Brand]: 'brand',
  [TrafficDropdownOption.WeekId]: 'week'
};

export enum TrafficTypeDisplayName {
  totalClicks = 'Total Traffic',
  organicClicks = 'Organic Traffic',
  adClicks = 'Paid Search Traffic',
  otherClicks = 'Other Traffic'
}

const HEAD_COLUMN_DEFINITION = {
  [TrafficDropdownOption.Keyword]: {
    headerDisplayName: 'Keyword',
    columnWidth: '360px'
  },
  [TrafficDropdownOption.Product]: {
    headerDisplayName: 'Product',
    columnWidth: '400px'
  },
  [TrafficDropdownOption.WeekId]: {
    headerDisplayName: 'Week',
    columnWidth: '150px'
  },
  [TrafficDropdownOption.Brand]: {
    headerDisplayName: 'Brand',
    columnWidth: '360px'
  }
};

const fields = [
  {
    name: 'organicClicks',
    displayName: 'Organic\nTraffic',
    wrapperStyle: {
      width: '93px'
    }
  },
  {
    name: 'adClicks',
    displayName: 'Paid Search\nTraffic',
    wrapperStyle: {
      width: '93px'
    }
  },
  {
    name: 'otherClicks',
    displayName: 'Other\nTraffic',
    wrapperStyle: {
      width: '93px'
    }
  },
  {
    name: 'totalClicks',
    displayName: 'Total\nTraffic',
    wrapperStyle: {
      width: '93px'
    }
  },
  {
    name: 'adSpend',
    displayName: 'Paid Search\nSpend',
    wrapperStyle: {
      width: '93px'
    }
  }
];

export interface ProductMetadata {
  stacklineSku: string;
  title: string;
  retailerSku: string;
  imageUrl?: string;
  [key: string]: any;
}

export const AtlasV2TrafficProductGrid = ({ trafficType = 'totalClicks' }: { trafficType: string }) => {
  const [dropdownOption, setDropdownOption] = useState<TrafficDropdownOption>(TrafficDropdownOption.Keyword);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 20;
  const METADATA_FETCH_SIZE = 100;
  const retailerId = useRetailerId();
  const { isProductEntity } = useProductEntity();
  const { isBrandEntity } = useBrandEntity();
  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonTimePeriodShortDisplayName = useComparisonTimePeriodShortDisplayName();
  const hideSubtitleFromCtx = useChartContextHideSubtitle();
  const disableSubtitleFallbackFromCtx = useChartContextDisableSubtitleFallback();
  const { numberOfActiveFilters, activeFilterDisplayNames } = useBeaconFilters();
  const { app, retailer, entityService, filters, categories } = useAppSelector((state) => ({
    app: state.app,
    retailer: state.retailer,
    entityService: state.entityService,
    filters: state.filters,
    categories: state.categories
  }));

  const summarySubtitleText = useMemo(() => {
    if (!entityService.mainEntity) {
      return '';
    }

    const subtitleObj = buildSubtitleDisplayName(
      retailer,
      entityService.mainEntity as any,
      filters,
      categories,
      app
    ) as {
      subtitleDisplayName?: string;
      entityDisplayName?: string;
    };

    if (numberOfActiveFilters > 1 && getAppName() === AppName.Atlas) {
      return activeFilterDisplayNames;
    }

    return subtitleObj.subtitleDisplayName || subtitleObj.entityDisplayName || '';
  }, [activeFilterDisplayNames, app, categories, entityService.mainEntity, filters, numberOfActiveFilters, retailer]);

  const groupByOptions = useMemo(() => {
    if (isProductEntity) {
      if (trafficType === 'otherClicks') {
        return [TrafficDropdownOption.WeekId];
      }
      return [TrafficDropdownOption.Keyword, TrafficDropdownOption.WeekId];
    }
    if (trafficType === 'otherClicks') {
      return [TrafficDropdownOption.Product, ...(isBrandEntity ? [] : [TrafficDropdownOption.Brand])];
    }
    return [
      TrafficDropdownOption.Keyword,
      TrafficDropdownOption.Product,
      ...(isBrandEntity ? [] : [TrafficDropdownOption.Brand])
    ];
  }, [isBrandEntity, isProductEntity, trafficType]);

  useEffect(() => {
    if (!groupByOptions.includes(dropdownOption) && groupByOptions.length > 0) {
      setDropdownOption(groupByOptions[0]);
    }
  }, [groupByOptions, dropdownOption]);

  // Fetch traffic data using the Doris API
  const { data, isLoading, getValuesByFieldName, getValueDifferencesByFieldName } = useDorisTableData({
    apiMethodName:
      isProductEntity && dropdownOption === TrafficDropdownOption.WeekId
        ? 'gettrafficsummarybyweek'
        : 'GetTrafficCountSummaryByType',
    groupByFieldName: dropdownOption === TrafficDropdownOption.WeekId ? 'product' : dropdownOption,
    metricType: trafficType,
    fields
  });

  const slicedData = useMemo(() => {
    if (isLoading) {
      return [];
    }

    return data.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  }, [currentPage, data, isLoading]);

  // Extract SKUs for metadata fetching when in Product view
  const skusToFetch = useMemo(() => {
    if (dropdownOption !== TrafficDropdownOption.Product) {
      return [];
    }

    const startChunk = Math.floor(((currentPage - 1) * ITEMS_PER_PAGE) / METADATA_FETCH_SIZE);
    const endChunk = Math.floor((currentPage * ITEMS_PER_PAGE - 1) / METADATA_FETCH_SIZE);

    // Get all SKUs needed for the current view and potential next pages
    const allSkus: string[] = [];
    for (let i = startChunk; i <= endChunk; i++) {
      const chunkStart = i * METADATA_FETCH_SIZE;
      const chunkEnd = chunkStart + METADATA_FETCH_SIZE;

      const chunkSkus = data
        .slice(chunkStart, chunkEnd)
        .map((item) => item.fieldKey)
        .filter(Boolean);

      allSkus.push(...chunkSkus);
    }

    return allSkus;
  }, [data, dropdownOption, currentPage]);

  const { data: productMetadata, isLoading: isMetadataLoading } = useQuery({
    queryKey: [skusToFetch],
    queryFn: async () => {
      if (!skusToFetch.length) return {};

      const { data: response } = await axios.post<ProductMetadata[]>('/api/atlas/GetEntityMetadataInBulk', {
        entityType: 'product',
        entityIds: skusToFetch,
        retailerId
      });

      return response.reduce((acc, product) => {
        acc[product.stacklineSku] = product;
        return acc;
      }, {} as Record<string, ProductMetadata>);
    },
    enabled: dropdownOption === TrafficDropdownOption.Product && skusToFetch.length > 0
  });

  // when it is product, we need to fetch metadata for each product, and append it to the data
  const enrichedData = useMemo(() => {
    if (dropdownOption !== TrafficDropdownOption.Product || !productMetadata) {
      return slicedData;
    }

    return slicedData.map((item) => ({
      ...item,
      metadata: {
        product: productMetadata[item.fieldKey]
      }
    }));
  }, [dropdownOption, slicedData, productMetadata]);

  const getPagedValuesByFieldName = useCallback(
    (fieldName: string) => {
      return getValuesByFieldName(fieldName).slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
    },
    [currentPage, getValuesByFieldName]
  );

  const getPagedValueDifferencesByFieldName = useCallback(
    (fieldName: string) => {
      return getValueDifferencesByFieldName(fieldName).slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
      );
    },
    [currentPage, getValueDifferencesByFieldName]
  );

  const handleNextPage = () => {
    const adjustedPage = ((currentPage - 1) % 1000) + 1;
    const dataStartIndex = (adjustedPage - 1) * ITEMS_PER_PAGE;

    if (!isLoading && data.slice(dataStartIndex, dataStartIndex + ITEMS_PER_PAGE).length < ITEMS_PER_PAGE) {
      return;
    }

    setCurrentPage(currentPage + 1);
  };

  const handlePreviousPage = () => {
    setCurrentPage(Math.max(1, currentPage - 1));
  };

  const headColumnDefinition = useMemo(() => {
    switch (dropdownOption) {
      case TrafficDropdownOption.Brand:
        return {
          field: 'brand',
          headerName: 'Brand',
          tableColumnProps: {
            style: {
              width: '360px'
            },
            align: 'left'
          },
          valueFormatter: (_, row: { brandName: string; brandId: string }) => {
            const Image = () => {
              const [error, setError] = useState(false);

              if (error) {
                return <BeaconProImageFallback style={brandColumnImageStyles} />;
              }

              return (
                <img
                  src={getBrandImageUrl(row.brandId)}
                  style={brandColumnImageStyles}
                  alt={row.brandName}
                  onError={() => setError(true)}
                />
              );
            };

            return (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Image />
                <EntityLink id={row.brandId} entityType="brand">
                  {row.brandName}
                </EntityLink>
              </div>
            );
          }
        };

      case TrafficDropdownOption.Product:
        return {
          headerName: 'Product',
          valueFormatter: (_, row: { product: { id: string; title: string } }) => {
            return (
              <div
                className="product-col"
                style={{ width: HEAD_COLUMN_DEFINITION[TrafficDropdownOption.Product].columnWidth }}
              >
                <ProductCell stacklineSku={row.product?.id} title={row.product?.title} />
              </div>
            );
          }
        };

      case TrafficDropdownOption.WeekId:
        return {
          headerName: 'Week',
          valueFormatter: (_, row: any) => {
            const { weekId } = row.responseRow;
            const { endDate } = getStartAndEndOfWeek(weekId);
            const weekEndingDate = formatTime(endDate, 'MMM D, YYYY');
            return (
              <div
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                <span style={{ margin: 0 }} title={weekEndingDate}>
                  {weekEndingDate}
                </span>
              </div>
            );
          }
        };

      case TrafficDropdownOption.Keyword:
      default:
        return {
          headerName: 'Keyword',
          valueFormatter: (_, row: { name: string }) => (
            <div
              className="keyword-col"
              style={{
                width: HEAD_COLUMN_DEFINITION[TrafficDropdownOption.Keyword].columnWidth,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              <TableSpan title={row.name}>{row.name}</TableSpan>
            </div>
          )
        };
    }
  }, [dropdownOption]);

  return (
    <Flex flexDirection="column" gap={16}>
      <AtlasV2MetricListContainer
        skeletonRows={20}
        showGrid={dropdownOption === TrafficDropdownOption.Product}
        renderGrid={(args) => {
          return <ProductGrid {...args} />;
        }}
        indexName="traffic"
        sortFieldName={trafficType}
        fields={
          dropdownOption === TrafficDropdownOption.Product || trafficType === 'otherClicks'
            ? fields
            : fields.filter((field) => field.name !== 'otherClicks')
        }
        entityTableHeaderProps={{
          prefixTitle: `${TrafficTypeDisplayName[trafficType]} by`,
          title: `${TrafficTypeDisplayName[trafficType]} by ${
            HEAD_COLUMN_DEFINITION[groupByOptions[0]].headerDisplayName
          }`,
          subTitle: hideSubtitleFromCtx
            ? disableSubtitleFallbackFromCtx
              ? undefined
              : () =>
                  summarySubtitleText ? (
                    <Text variant="body2" sx={{ marginTop: '8px' }}>
                      {summarySubtitleText}
                    </Text>
                  ) : (
                    ''
                  )
            : () => {
                return (
                  <BeaconSubtitleContainer wrapperStyle={{ marginBottom: '0px' }}>
                    <Text variant="body1" lineHeight="24px">
                      {`The table displays the ${TrafficTypeDisplayName[
                        trafficType
                      ].toLocaleLowerCase()} for ${entityPhrase} by ${
                        subTitleDisplayName[dropdownOption]
                      } ${taxonomyText} during the ${mainTimePeriodShortDisplayName} period compared to the ${comparisonTimePeriodShortDisplayName}.`}
                      <SubtitleFilters showFilterNumber={false} />
                    </Text>
                  </BeaconSubtitleContainer>
                );
              },
          groupByFields: groupByOptions.map((option) => ({
            name: option,
            displayName: HEAD_COLUMN_DEFINITION[option].headerDisplayName
          })),
          handleGroupByChange: (event) => {
            setDropdownOption(event.target.value as TrafficDropdownOption);
            setCurrentPage(1);
          },
          customExportModal: (props: { open: boolean; close: () => void }) => (
            <AtlasV2DorisTableTrafficV2ExportModal open={props.open} onClose={props.close} />
          )
        }}
        headColumnDefinition={headColumnDefinition}
        customDataSource={{
          data: enrichedData,
          isLoading: isLoading || (dropdownOption === TrafficDropdownOption.Product && isMetadataLoading),
          getValuesByFieldName: getPagedValuesByFieldName,
          getValueDifferencesByFieldName: getPagedValueDifferencesByFieldName
        }}
        useTableDataProps={{
          itemsPerPage: 1000
        }}
        postProcessData={(data) => {
          if (isProductEntity && dropdownOption === TrafficDropdownOption.WeekId) {
            return data.sort((a, b) => b.responseRow?.weekId - a.responseRow?.weekId);
          }
          return data;
        }}
        shouldHidePagination
        iconList={[IconsList.export, IconsList.tile, IconsList.table]}
      />
      <Flex justifyContent="flex-end">
        <Pagination
          currentPage={currentPage}
          shouldUseInfinitePagination
          onClickNext={handleNextPage}
          capPage={50}
          onClickPrevious={handlePreviousPage}
        />
      </Flex>
    </Flex>
  );
};
